import { useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import type { MeetingPageOfflineRecording } from '@wundo/shared/meetingPage.ts';
import { storedMicrophoneId } from '@/lib/audioDevices.ts';
import { useOfflineRecording } from '@/lib/useOfflineRecording.ts';
import type { MeetingOfflineOffer } from './MeetingNotesControl.tsx';

/*
 * Every way off this page costs a running recording, wherever it leads. Held
 * out here so the router is not resubscribed on every render.
 */
const LEAVING_COSTS_THE_RECORDING = () => true;

/* What the page assumes about microphone recording while it is still loading. */
const NO_OFFLINE_RECORDING: MeetingPageOfflineRecording = {
  status: 'idle',
  started_at: null,
  next_chunk_index: 0,
};

/**
 * Which question about recording is on screen: what replacing the recording
 * already made here costs, which microphone to start with, or whether to pick
 * an interrupted recording back up.
 */
export type MeetingRecordingAsk = 'replace' | 'setup' | 'resume' | null;

export interface MeetingRecordingOptions {
  userId: string;
  meetingId: string;
  offline: MeetingPageOfflineRecording | undefined;
  onChanged: () => void;
  /* Stopping is asking for the verslag. */
  onStopped: () => void;
}

export type MeetingRecording = ReturnType<typeof useMeetingRecording>;

/* A recording running on this device, or an interrupted one that still holds audio. */
export interface MeetingRunningRecording {
  /* False for an interrupted recording, which waits to be resumed. */
  live: boolean;
  elapsed: number;
  chunkLost: boolean;
}

/**
 * The meeting page's microphone recording: the recorder, the questions around
 * starting one, the live view that can fold back into its pill, and the hold
 * on in-app navigation while it runs (leaving unmounts the recorder).
 */
export function useMeetingRecording({
  userId,
  meetingId,
  offline,
  onChanged,
  onStopped,
}: MeetingRecordingOptions) {
  const recorder = useOfflineRecording({
    userId,
    meetingId,
    offline: offline ?? NO_OFFLINE_RECORDING,
    onChanged,
  });
  const [ask, setAsk] = useState<MeetingRecordingAsk>(null);
  /* A recording opens in front of the page; the pill is the way back to it. */
  const [minimized, setMinimized] = useState(false);

  const state = recorder.state;
  /* Closing the tab is the browser's own warning, which the recorder puts up. */
  const leaving = useBlocker({
    shouldBlockFn: LEAVING_COSTS_THE_RECORDING,
    disabled: state !== 'recording',
    enableBeforeUnload: false,
    withResolver: true,
  });

  /* A running or interrupted recording has its own control; the rest is an offer. */
  const running = state === 'recording' || state === 'interrupted';
  const offer: MeetingOfflineOffer = running ? null : state;
  const runningRecording: MeetingRunningRecording | null = running
    ? {
        live: state === 'recording',
        elapsed: recorder.elapsed,
        chunkLost: recorder.chunkLost,
      }
    : null;

  const opened = () => {
    setAsk(null);
    setMinimized(false);
  };
  const closeAsk = () => setAsk(null);

  return {
    recorder,
    ask,
    setAsk,
    minimized,
    setMinimized,
    leaving,
    runningRecording,
    offer,
    pending: recorder.starting || recorder.stopping,
    /* On a meeting that already has a recording the cost comes first. */
    askToRecord: () => setAsk(state === 'done' ? 'replace' : 'setup'),
    askToResume: () => setAsk('resume'),
    /* Unfold the running recording from its pill. */
    openLive: () => setMinimized(false),
    start: (deviceId: string | null) => recorder.start(deviceId, opened, closeAsk),
    /* Resuming takes the microphone chosen last, without asking again. */
    resume: () => recorder.start(storedMicrophoneId(), opened, closeAsk),
    stop: () => {
      recorder.stop();
      onStopped();
    },
    /* The microphone is let go of first, so what was said becomes a verslag. */
    leave: () => {
      recorder.stop();
      leaving.proceed?.();
    },
  };
}
